import { useMutation } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import {
  ICreateKvitteringPayload,
  IFordelKvitteringTilKollegaPayload,
  IHentKvitteringerFraKollegaPayload,
  IUpdateKvitteringPayload,
} from '../types'
import { IKvitteringData } from '../../../ducks/kvittering/types'
import { kvitteringKeys, kvitteringQueryHelpers } from '../queries/helpers'
import { kvitteringApi } from '../queries/kvittering-api'
import { useDispatch } from 'react-redux'
import { kvitteringActions } from '../../../ducks/kvittering/actions'
import {
  deleteKvitteringFailedToast,
  deleteKvitteringSuccessToast,
  fordelKvitteringFailedToast,
  fordelKvitteringSuccessToast,
  hentKvitteringerFailedToast,
  hentKvitteringerSuccessToast,
  kvitteringArkiveringFailedToast,
  kvitteringFerdigstillingFailedToast,
  useSingleToast,
} from '../../../common/toast'
import { useOptimisticDebounceKvitteringMutate } from './query-hooks'
import { useCallback } from 'react'
import { DEBOUNCE_MS } from '../../../constants'
import { useGoBack } from '../../../common/navigation'

export {
  useCreateKvitteringMutation,
  useUpdateKvittering,
  useForsideDeleteKvittering,
  useFordelKvitteringTilKollega,
  useHentKvitteringerFraKollega,
  useDebouncedKvitteringUpdate,
  useArkiverKvittering,
  useFerdigstillKvittering,
}

const useCreateKvitteringMutation = () => {
  const dispatch = useDispatch()

  return useMutation<
    string,
    string | Error,
    ICreateKvitteringPayload,
    { previousKvittering: IKvitteringData }
  >({
    mutationKey: kvitteringKeys.create(),
    mutationFn: async (payload: ICreateKvitteringPayload) => {
      const { headers } = await kvitteringApi.post(payload)

      const kvitteringId = headers.location.match(/(\d+)$/)?.pop()

      return kvitteringId
    },
    onSuccess: (kvitteringId) => {
      dispatch(kvitteringActions.setCurrentKvitteringId(kvitteringId))
      return kvitteringQueryHelpers.invalidateKvittering(kvitteringId)
    },
  })
}

const useUpdateKvittering = () => {
  return useMutation<
    AxiosResponse<null>,
    string | Error,
    IUpdateKvitteringPayload
  >({
    mutationKey: kvitteringKeys.update(),
    mutationFn: kvitteringApi.put,
    onSuccess: async (_, { kvittering }) => {
      const id = kvittering?.id

      if (id) {
        await kvitteringQueryHelpers.invalidateKvittering(id)
      }
    },
  })
}

const useDebouncedKvitteringUpdate = (kvitteringId: string) => {
  const mutation = useMutation<
    AxiosResponse<null>,
    string | Error,
    IUpdateKvitteringPayload
  >({
    mutationKey: kvitteringKeys.debouncedUpdate(),
    mutationFn: kvitteringApi.put,
    onSuccess: async (_, { kvittering }) => {
      const id = kvittering?.id

      if (id) {
        await kvitteringQueryHelpers.invalidateKvittering(id)
      }
    },
  })

  const optimisticDebounceUpdate = useOptimisticDebounceKvitteringMutate(
    mutation.mutate,
    kvitteringId ?? '',
    (draft, { kvittering }) => {
      if (!draft) return draft

      return kvittering
    }
  )

  const updateFn = useCallback(
    (payload: IUpdateKvitteringPayload) => {
      const updateKey = JSON.stringify(kvitteringKeys.debouncedUpdate()[0])

      optimisticDebounceUpdate(payload, {
        debounceMs: DEBOUNCE_MS,
        key: `${updateKey}_${payload.kvittering.id}`,
      })
    },
    [optimisticDebounceUpdate]
  )

  return { ...mutation, mutate: updateFn }
}

const useForsideDeleteKvittering = (kvittering: IKvitteringData) => {
  const singleToast = useSingleToast()

  return useMutation<AxiosResponse<null>, string | Error>({
    mutationKey: kvitteringKeys.delete(kvittering.id ?? ''),
    mutationFn: async () => kvitteringApi.delete(kvittering.id ?? ''),
    onMutate: async () => {
      kvitteringQueryHelpers.updateForsideKvitteringerCache((draft) =>
        draft.filter((k) => k.id !== kvittering.id)
      )
    },
    onSuccess: async () => {
      singleToast.showToast(deleteKvitteringSuccessToast())
    },
    onError: async () => {
      singleToast.showToast(deleteKvitteringFailedToast())
      kvitteringQueryHelpers.updateForsideKvitteringerCache((draft) => [
        ...draft,
        kvittering,
      ])
    },
  })
}

const useFordelKvitteringTilKollega = (kvittering: IKvitteringData) => {
  const singleToast = useSingleToast()

  return useMutation<
    AxiosResponse<null>,
    string | Error,
    IFordelKvitteringTilKollegaPayload
  >({
    mutationKey: kvitteringKeys.fordel(kvittering.id ?? ''),
    mutationFn: async (vars) => kvitteringApi.fordel(kvittering.id ?? '', vars),
    onMutate: async () =>
      kvitteringQueryHelpers.updateForsideKvitteringerCache((draft) =>
        draft.filter((k) => k.id !== kvittering.id)
      ),
    onSuccess: async (_, { ansatt }) => {
      singleToast.showToast(fordelKvitteringSuccessToast(ansatt.navn))
    },
    onError: async () => {
      singleToast.showToast(fordelKvitteringFailedToast())
      kvitteringQueryHelpers.updateForsideKvitteringerCache((draft) => [
        ...draft,
        kvittering,
      ])
    },
  })
}

const useHentKvitteringerFraKollega = () => {
  const singleToast = useSingleToast()

  return useMutation<
    AxiosResponse<null>,
    string | Error,
    IHentKvitteringerFraKollegaPayload
  >({
    mutationKey: kvitteringKeys.hent(),
    mutationFn: async (vars) => kvitteringApi.overta(vars),
    onSuccess: async (_, { kollega }) => {
      singleToast.showToast(hentKvitteringerSuccessToast(kollega))
      await kvitteringQueryHelpers.invalidateForsideKvitteringer()
    },
    onError: () => {
      singleToast.showToast(hentKvitteringerFailedToast())
    },
  })
}

const useArkiverKvittering = (kvittering?: IKvitteringData) => {
  const singleToast = useSingleToast()
  const dispatch = useDispatch()
  const goBack = useGoBack()

  return useMutation<AxiosResponse<null>, string | Error>({
    mutationKey: kvitteringKeys.arkiver(kvittering?.id ?? ''),
    mutationFn: async () => kvitteringApi.arkiver(kvittering?.id ?? ''),
    onSuccess: async () => {
      if (kvittering) {
        const { id } = kvittering

        await kvitteringQueryHelpers.invalidateKvittering(id ?? '')
      }
    },
    onError: () => {
      singleToast.showToast(kvitteringArkiveringFailedToast())
      dispatch(kvitteringActions.clearCurrentKvitteringId())
      goBack()
    },
  })
}

const useFerdigstillKvittering = (kvittering?: IKvitteringData) => {
  const singleToast = useSingleToast()
  const dispatch = useDispatch()
  const goBack = useGoBack()

  return useMutation<AxiosResponse<null>, string | Error>({
    mutationKey: kvitteringKeys.ferdigstill(kvittering?.id ?? ''),
    mutationFn: async () => kvitteringApi.ferdigstill(kvittering?.id ?? ''),
    onSuccess: async () => {
      if (kvittering) {
        const { id } = kvittering

        await kvitteringQueryHelpers.invalidateKvittering(id ?? '')
      }
    },
    onError: () => {
      singleToast.showToast(kvitteringFerdigstillingFailedToast())
      dispatch(kvitteringActions.clearCurrentKvitteringId())
      goBack()
    },
  })
}
